import type { AttributionType } from 'app/campaign/campaign.model';
import type { EngagementTier } from './data-platform.utils';

/** Predefined dimension type names. Only types with actual data are shown in the UI. */
export const DIMENSION_TYPES = [
  'pageType',
  'category',
  'tags',
  'brand',
  'gender',
  'color',
  'size',
  'material',
  'author',
] as const;

/** Filters on the finalized session row: values within a field OR, fields AND. */
export interface DataPlatformSessionFilter {
  /** direct, organic_search, paid_search, ... */
  sources?: string[];
  /** Hosts, each also matching its subdomains, or SOCIAL_SITES keys. */
  referrerDomains?: string[];
  /** ISO 3166-1 alpha-2, upper case. */
  countries?: string[];
  /** Countries the visit is not from; a visit without a country matches. */
  excludedCountries?: string[];
  /** desktop | mobile | tablet */
  deviceTypes?: string[];
  utmSources?: string[];
  utmMediums?: string[];
  /** Inclusive, seconds. */
  minDurationSeconds?: number;
  /** Inclusive, seconds; 0 alone selects bounces. */
  maxDurationSeconds?: number;
  loginStatus?: DataPlatformSessionLoginStatus;
  coupon?: 'shown' | 'copied';
}

/** Filter spelling of the login state, matching the login_status facet labels. */
export const DP_SESSION_LOGIN_STATUS = {
  loggedIn: 'logged_in',
  loggedOut: 'logged_out',
} as const;

export type DataPlatformSessionLoginStatus = (typeof DP_SESSION_LOGIN_STATUS)[keyof typeof DP_SESSION_LOGIN_STATUS];

/** Accepted only by the visits and profiles lists, counts and exports; never holds an included value. */
export interface DataPlatformExcludeFilter {
  goals?: string[];
  utmCampaigns?: string[];
  /** "type:value" composites. */
  dimensions?: string[];
  /** Visits only. */
  tags?: string[];
  /** Visits only; the same values as session.referrerDomains. */
  referrerDomains?: string[];
}

/** Option lists for the visit facets; device types are a fixed set. */
export interface DataPlatformSessionFilterOptions {
  sessionSources: string[];
  referrerApps: string[];
  referrerDomains: string[];
  countries: string[];
  deviceTypes: string[];
  utmSources: string[];
  utmMediums: string[];
  /** The site tracks login state. */
  hasLoginData: boolean;
  /** The site runs coupon campaigns. */
  hasCoupons: boolean;
}

// Shared filter
export interface DataPlatformEventFilter {
  siteIds: number[];
  tags?: string[];
  from: string; // ISO datetime
  to?: string;

  /** Wildcard pattern for identifier/email: john* | *john | *john* | exact */
  identifierFilter?: string;
  /** Restricts the profiles list to one uid. Profiles only; visits take their uid on the request. */
  uid?: number;
  /** Goal names (OR logic) */
  goals?: string[];
  /** UTM campaign names (OR logic) */
  utmCampaigns?: string[];
  /** Widget IDs (OR logic). Filters visits/profiles to sessions that contain an event from any of these widgets. */
  widgetIds?: number[];
  /** Widget event types filter (OR logic). When combined with widgetIds, filters to sessions containing a matching widget+eventType event. */
  widgetEventTypes?: string[];
  /** Action dimensions (OR logic). Each entry is a "type:value" composite string. */
  dimensions?: string[];
  /** Filter profiles with at least this many visits */
  minVisits?: number;
  /** Filter profiles with at most this many visits */
  maxVisits?: number;
  /** Engagement-score bands (0–100), derived from the selected intent tiers (see tiersToScoreBands).
   *  A row matches when its score falls in ANY band — supports non-contiguous tier selections. */
  engagementScoreBands?: DataPlatformScoreBand[];
  /** Consent status filter: 'has_consent' | 'has_not_consent' */
  consentFilter?: string;
  consentVersionDate?: string;
  /** 'identified' | 'anonymous'; omitted means all. Visits read the session row's flag, Profiles id_map. */
  identifiedStatus?: string;
  /** Predicates on the finalized session row. Setting any of these routes the query to events. */
  session?: DataPlatformSessionFilter;
  /** true = with a purchase, false = without; omitted means no filter. */
  hasPurchase?: boolean;
  /** Person has at least this many purchases, within lastPurchaseWithinDays when set. */
  minPurchaseCount?: number;
  /** Person bought within this many days; the count and the Profiles amount count within it. */
  lastPurchaseWithinDays?: number;
  /** Inclusive purchase total, buyers only: the visit's on Visits, the person's on Profiles. */
  minPurchaseAmount?: number;
  maxPurchaseAmount?: number;
  /** Person's latest NPS (0-10) / CSAT (1-5) score, inclusive bounds. */
  minNpsScore?: number;
  maxNpsScore?: number;
  minCsatScore?: number;
  maxCsatScore?: number;
  /** How the person's identifier was first obtained (OR): 'onsite' | 'manual_import' | 'migration' | an integration name. */
  identitySources?: string[];
  exclude?: DataPlatformExcludeFilter;
  /** Profiles only, ISO, inclusive: last seen at or after this. */
  lastSeenFrom?: string;
  /** Profiles only, ISO, exclusive: last seen before this. */
  lastSeenTo?: string;
  /** /query only: every visit of the people who match all the rules today; meta.audience echoes the id. */
  audience?: DataPlatformAudienceDefinition;
}

/** An inclusive engagement-score range. */
export interface DataPlatformScoreBand {
  min: number;
  max: number;
}

/** Unified filter + page state emitted by FilterBarComponent */
export interface DataPlatformViewFilter {
  identifierFilter?: string;
  tags?: string[];
  goals?: string[];
  utmCampaigns?: string[];
  /** Widget IDs (OR logic) */
  widgetIds?: number[];
  /** Widget event types (OR logic). Combined with widgetIds: filter visits that fired one of these event types from any of the selected widgets. */
  widgetEventTypes?: string[];
  dimensions?: string[];
  minVisits?: number;
  maxVisits?: number;
  /** Engagement-score bands (0–100), derived from the selected intent tiers. */
  engagementScoreBands?: DataPlatformScoreBand[];
  /** The tiers behind the bands, for the filter link. Never sent to the API. */
  tiers?: EngagementTier[];
  /** Consent status filter: 'has_consent' | 'has_not_consent' */
  consentFilter?: string;
  consentVersionDate?: string;
  /** Identity filter (Profiles view): 'identified' | 'anonymous'. Omitted means all profiles. */
  identifiedStatus?: string;
  /** Profiles view only: when true, the list/export universe expands to every identified profile
   *  (including those with no recent activity). Behavioral filters do not apply in this mode. */
  includeInactive?: boolean;
  /** Profiles view only: when true, only contacts never seen on the site are listed. */
  notYetSeen?: boolean;
  /** Visits view only. */
  session?: DataPlatformSessionFilter;
  /** Visits: purchase in the visit; Profiles: has ever purchased. */
  hasPurchase?: boolean;
  /** Profiles view only. */
  minPurchaseCount?: number;
  /** Profiles view only. */
  lastPurchaseWithinDays?: number;
  minPurchaseAmount?: number;
  maxPurchaseAmount?: number;
  /** Profiles view only; the person's latest score, inclusive bounds. */
  minNpsScore?: number;
  maxNpsScore?: number;
  minCsatScore?: number;
  maxCsatScore?: number;
  /** See DataPlatformEventFilter. */
  identitySources?: string[];
  exclude?: DataPlatformExcludeFilter;
  page: number;
  pageSize: number;
}

// Graph
export interface DataPlatformGraphRequest {
  filter: DataPlatformEventFilter;
  granularity?: string; // hour | day | week | month
  eventType?: string;
  metric?: string; // count | unique_users | sessions | revenue | avg_session_duration
  widgetIds?: number[];
  dimensionFilter?: string;
  groupByDimension?: string;
  referrerType?: string;
  referrerDomain?: string;
  utmCampaignName?: string;
  identifiedStatus?: string; // 'identified' | 'anonymous'
  actionTitle?: string;
  /** Grouped graphs only: keep the strongest series, ranked by their total over the range (1 to 100). */
  topN?: number;
  /** groupByDimension 'dimensions' only: keep labels of these types (the part before ':'), applied before the ranking. */
  dimensionTypes?: string[];
}
export interface DataPlatformGraphDataPoint {
  timestamp: string;
  value: number;
}
export interface DataPlatformGraphResponse {
  series: DataPlatformGraphSeries[];
  /** True when topN cut series away. */
  truncated?: boolean;
}

// Bounce graph
export interface DataPlatformBounceGraphRequest {
  filter: DataPlatformEventFilter;
  granularity?: string; // hour | day | week | month
}
export interface DataPlatformBounceGraphResponse {
  visits: DataPlatformGraphDataPoint[];
  bounces: DataPlatformGraphDataPoint[];
}

// Holdout impact graph — six series (revenue/orders/visits, exposed/holdout) in a single response.
// Powers both the analytics page revenue chart (uses 2 of 6 series) and a future port of
// app-revenue-impact (uses all 6 to derive conversion-rate / AOV / RPV uplift).
export interface DataPlatformHoldoutImpactGraphRequest {
  filter: DataPlatformEventFilter;
  granularity?: string; // hour | day | week | month
}
export interface DataPlatformHoldoutImpactGraphResponse {
  exposedRevenue: DataPlatformGraphDataPoint[];
  holdoutRevenue: DataPlatformGraphDataPoint[];
  exposedOrders: DataPlatformGraphDataPoint[];
  holdoutOrders: DataPlatformGraphDataPoint[];
  exposedVisits: DataPlatformGraphDataPoint[];
  holdoutVisits: DataPlatformGraphDataPoint[];
}

// Chart helpers
export interface DataPlatformGraphSeries {
  label: string;
  dataPoints: DataPlatformGraphDataPoint[];
}

// Breakdown
export interface DataPlatformBreakdownRequest {
  filter: DataPlatformEventFilter;
  dimension: string; // event_type | action_title | geo_country | referrer_type | session_source | referrer_app | device_type | utm_campaign_name | referrer_domain | browser | os | widget_id | session_duration | dimensions
  metric?: string; // count | unique_users | sessions | revenue
  eventType?: string;
  topN?: number;
  histogram?: boolean;
  widgetIds?: number[];
  dimensionFilter?: string;
  actionTitle?: string;
}
export interface DataPlatformBreakdownRow {
  label: string;
  value: number;
}
export interface DataPlatformBreakdownResponse {
  rows: DataPlatformBreakdownRow[];
}

// Generic analytics query (POST data-platform/query). One call composes scalar / time-series /
// breakdown / grouped-series shapes from metric + facet names over the query filter. Response
// is a tidy table (rows keyed by snake_case column name) plus a self-describing meta block. At most
// one non-time facet is allowed. The constants below are the subset of the server registry this app
// uses.

/**
 * The shared event filter minus the per-visitor fields. /query aggregates rows and has no
 * per-visitor stage, so it rejects these three with a 400 (AnalyticsQueryEngine.BuildSpec).
 * Use the profiles endpoints for per-visitor filtering — note those are all-time scoped.
 */
export type DataPlatformQueryFilter = Omit<DataPlatformEventFilter, 'minVisits' | 'maxVisits' | 'engagementScoreBands'>;

export interface DataPlatformQueryRequest {
  filter: DataPlatformQueryFilter;
  metrics: DpMetric[];
  /** Group-by axes. 'time' is the time facet; at most one non-time facet alongside it. */
  facets?: DpFacet[];
  granularity?: 'hour' | 'day' | 'week' | 'month'; // default day; only valid with the 'time' facet
  topN?: number; // clamped 1..100, default 20; also caps the facet values of a grouped series
  orderBy?: { metric: string; dir: 'asc' | 'desc' };
  // Request-level scope filters — setting any routes the query to the events table (92-day cap).
  eventType?: string;
  actionTitle?: string;
  goalName?: string;
  referrerType?: string;
  referrerDomain?: string;
  utmCampaignName?: string;
  utmCampaignSource?: string;
  dimensionFilter?: string;
  identifiedStatus?: string; // 'identified' | 'anonymous'
  purchaseGoals?: string[];
}

export interface DataPlatformQueryColumn {
  name: string;
  /** 'attribute': a value resolved per facet value, like page's url. */
  role: 'time' | 'facet' | 'attribute' | 'metric';
}

/** Facet labels are strings, the time column ('period') is an ISO timestamp string, metrics are
 *  numbers — or null when a derived metric's denominator is 0 (no data, not zero). */
export type DataPlatformQueryRow = Record<string, string | number | null>;

export interface DataPlatformQueryResponse {
  columns: DataPlatformQueryColumn[];
  rows: DataPlatformQueryRow[];
  meta: DataPlatformResponseMeta;
  /** True when a breakdown hit the topN cap (more rows exist). */
  truncated: boolean;
}

export interface DataPlatformQueryOptions {
  /** Skips the cut to the events window; the caller handles a refusal. */
  wholeWindow?: boolean;
}

export interface DataPlatformQueryWidgetLabel {
  widgetId: number;
  widgetName: string | null;
  campaignId: number;
  campaignName: string | null;
  attributionType?: AttributionType | null;
  thumbnail?: string | null;
}

export interface DataPlatformLabelledQueryResponse {
  query: DataPlatformQueryResponse;
  /** One per widget_id row, from every account in the filter; a deleted widget has none. */
  widgets: DataPlatformQueryWidgetLabel[];
}

/** Metric vocabulary used by the analytics page (subset of the backend registry). */
export const DP_METRICS = {
  count: 'count',
  uniqueUsers: 'unique_users',
  /** First-ever activity in the bucket; also the series label visitor_lifecycle returns. */
  newVisitors: 'new_visitors',
  /** Active in the period, first seen before it. events/scalar, site + date only. */
  returningVisitors: 'returning_visitors',
  /** The new / returning split as two series over time. events/graph, site + date only. */
  visitorLifecycle: 'visitor_lifecycle',
  sessions: 'sessions',
  pageviews: 'pageviews',
  bounces: 'bounces',
  identifiedSessions: 'identified_sessions',
  anonymousSessions: 'anonymous_sessions',
  identificationRate: 'identification_rate',
  loggedInSessions: 'logged_in_sessions',
  determinedSessions: 'determined_sessions',
  /** Denominator excludes sessions with an unknown login state. */
  loggedInRate: 'logged_in_rate',
  loginDeterminationCoverage: 'login_determination_coverage',
  revenue: 'revenue',
  /** Purchase events, the account's purchase goal included. */
  purchases: 'purchases',
  purchaseRate: 'purchase_rate',
  averageOrderValue: 'average_order_value',
  /** Rollup-only, so the events-window cap does not apply. */
  widgetLastTouchRevenue: 'widget_last_touch_revenue',
  widgetLastTouchPurchases: 'widget_last_touch_purchases',
  /** Revenue from purchases that used a coupon the widget handed out. */
  widgetIncentivizedRevenue: 'widget_incentivized_revenue',
  widgetOpens: 'widget_opens',
  widgetConversions: 'widget_conversions',
  widgetConversionsPartial: 'widget_conversions_partial',
  widgetClickthroughs: 'widget_clickthroughs',
  avgSessionDuration: 'avg_session_duration',
  signupsSubscribers: 'signups_subscribers',
  signupsMembers: 'signups_members',
  signupsDuplicatesPrevented: 'signups_duplicates_prevented',
  holdoutRevenue: 'holdout_revenue',
  exposedRevenue: 'exposed_revenue',
  holdoutOrders: 'holdout_orders',
  exposedOrders: 'exposed_orders',
  holdoutSessions: 'holdout_sessions',
  exposedSessions: 'exposed_sessions',
  /** Distinct visitors who saw any campaign. Per-day values do not sum to the period total. */
  campaignExposedVisitors: 'campaign_exposed_visitors',
  /** Distinct visitors with a last-touch-credited purchase. Rollup-only; per-day values do not sum. */
  campaignAttributedBuyers: 'campaign_attributed_buyers',
  /** campaign_attributed_buyers / campaign_exposed_visitors, 0..1. Null when nobody was exposed. */
  campaignVisitorPurchaseRate: 'campaign_visitor_purchase_rate',
  // Customer retention family: person-keyed via id_map; ratios are null (not 0) without a basis.
  /** Distinct customers with a purchase in range. Per-day values do not sum to the period total. */
  uniqueBuyers: 'unique_buyers',
  /** returning_buyers / unique_buyers, 0..1: share of the range's buyers who had bought before. */
  repeatPurchaseRate: 'repeat_purchase_rate',
  /** Mean days from first to second purchase, over customers whose second purchase fell in range. */
  timeToSecondPurchase: 'time_to_second_purchase',
  /** Mean days from a purchase in range to the customer's next site visit. */
  timeBetweenVisits: 'time_between_visits',
  /** Numerator of time_between_visits; adds up across days. */
  purchaseReturnGapDays: 'purchase_return_gap_days',
  /** Denominator of time_between_visits; adds up across days. */
  purchasesWithReturn: 'purchases_with_return',
  /** Mean visits per active existing customer over the range. Scale by 30 / days-in-range for per-month. */
  customerVisitRate: 'customer_visit_rate',
} as const;

/**
 * Facet (group-by axis) vocabulary. /query accepts at most one non-time facet, so a per-facet trend
 * is ['time', <facet>]. referrerType, goalName, deviceType, sessionSource, browser and os are
 * mirrored in daily_stats; the ones marked events-only read the events table.
 */
export const DP_FACETS = {
  time: 'time',
  siteId: 'site_id',
  referrerType: 'referrer_type',
  goalName: 'goal_name',
  deviceType: 'device_type',
  sessionSource: 'session_source',
  browser: 'browser',
  os: 'os',
  /** Events-only. */
  utmCampaignName: 'utm_campaign_name',
  /** Events-only; the utm_source parameter as the marketer tagged it. */
  utmCampaignSource: 'utm_campaign_source',
  /** Events-only. */
  referrerDomain: 'referrer_domain',
  /** Page title; rows also carry url, its most viewed path or null, and url_share. */
  page: 'page',
  /** Events-only. Labels are logged_in / logged_out / unknown, carried on revenue rows too. */
  loginStatus: 'login_status',
  /** Events-only. Labels are identified / anonymous, carried on revenue rows too. */
  identifiedStatus: 'identified_status',
  /** Rollup-only, with the widget metrics. */
  widgetId: 'widget_id',
} as const;

/** Metric names /query accepts. Add to DP_METRICS above before sending a new one. */
export type DpMetric = (typeof DP_METRICS)[keyof typeof DP_METRICS];

/** Facet names /query accepts. Add to DP_FACETS above before sending a new one. */
export type DpFacet = (typeof DP_FACETS)[keyof typeof DP_FACETS];

/** The backend serves events-table queries for at most this many days. */
export const EVENTS_WINDOW_DAYS = 92;

/** First day with login state; imported visits before it have none. */
export const LOGIN_DATA_SINCE = '2026-09-09';

/** Grouping by one of these puts a query under the events window. */
const EVENTS_ONLY_FACETS: readonly string[] = [
  DP_FACETS.utmCampaignName,
  DP_FACETS.utmCampaignSource,
  DP_FACETS.referrerDomain,
  DP_FACETS.loginStatus,
  DP_FACETS.identifiedStatus,
];

export function eventsWindowStart(to: string | Date | undefined): Date {
  const start = to ? new Date(to) : new Date();
  start.setDate(start.getDate() - (EVENTS_WINDOW_DAYS - 1));
  start.setHours(0, 0, 0, 0);
  return start;
}

/** Shortens a filter to the events window; untouched when it already fits. */
export function clampToEventsWindow<T extends { from: string; to?: string }>(filter: T): T {
  const earliest = eventsWindowStart(filter.to);
  return new Date(filter.from) < earliest ? { ...filter, from: earliest.toISOString() } : filter;
}

/**
 * Applied to every /query, so no caller has to know whether its facets read raw events.
 */
export function withEventsWindow(request: DataPlatformQueryRequest): DataPlatformQueryRequest {
  const readsEvents = (request.facets ?? []).some((facet) => EVENTS_ONLY_FACETS.includes(facet));
  return readsEvents ? { ...request, filter: clampToEventsWindow(request.filter) } : request;
}

/** A filter bound's UTC date and time as a local Date, the frame the API buckets in. */
export function utcFieldsAsLocal(bound: string): Date {
  const instant = new Date(bound);
  return new Date(
    instant.getUTCFullYear(),
    instant.getUTCMonth(),
    instant.getUTCDate(),
    instant.getUTCHours(),
    instant.getUTCMinutes(),
    instant.getUTCSeconds(),
    instant.getUTCMilliseconds(),
  );
}

// Funnel
export interface DataPlatformFunnelStep {
  eventType: string;
  widgetIds?: number[];
  goalName?: string;
  actionUrl?: string;
}
export interface DataPlatformFunnelRequest {
  filter: DataPlatformEventFilter;
  steps: DataPlatformFunnelStep[];
}
export interface DataPlatformFunnelStepResult {
  eventType: string;
  count: number;
  percentage: number;
}
export interface DataPlatformFunnelResponse {
  steps: DataPlatformFunnelStepResult[];
}

// Scalar
export interface DataPlatformScalarRequest {
  filter: DataPlatformEventFilter;
  metric?: string;
  eventType?: string;
  goalName?: string;
  dimensionFilter?: string;
  actionTitle?: string;
}
export interface DataPlatformScalarResponse {
  value: number;
  metric: string;
}

// Visits
/** Backend enum names, serialized as-is (PascalCase). */
export type DataPlatformLoginStatus = 'Unknown' | 'LoggedOut' | 'LoggedIn';
export interface DataPlatformVisitsRequest {
  filter: DataPlatformEventFilter;
  eventType?: string;
  uid?: number;
  /** When combined with uid, scopes the query to a single visit — used by the flyout to lazy-load events. */
  sessionId?: number;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  /** When false the server skips the count query and totalCount will be null. Default: true. */
  includeTotalCount?: boolean;
  /** Only totalCount is computed; visits comes back empty. */
  countOnly?: boolean;
  /**
   * When false, Events will be empty and pre-computed summary fields are populated instead.
   * Use false for list views to avoid transferring all raw event rows. Default: true.
   */
  includeEvents?: boolean;
  /**
   * When true, distributedCoupons is populated on includeEvents=false responses. Opt-in because
   * the extra column adds cost to high-volume list queries. includeEvents=true always returns it.
   */
  includeDistributedCoupons?: boolean;
}
export interface DataPlatformVisitEvent {
  timestamp: string;
  eventType: string;
  actionTitle: string;
  actionUrl: string;
  goalName: string;
  revenue: number | null;
  tags: string[];
  dimensions?: string[];
  widgetId?: number | null;
  loginStatus: DataPlatformLoginStatus;
}
export interface DataPlatformVisitRow {
  uid: number;
  sessionId: number;
  siteId?: number;
  identifier?: string;
  /** Contact display name (first + last), when known. */
  name?: string;
  /** Contact's stored email column, when known. May differ from identifier. */
  email?: string;
  startedAt: string;
  endedAt: string;
  sessionDuration: number;
  browser: string;
  os: string;
  deviceType: string;
  geoCountry: string;
  /** Owner of the visitor's network from the IP lookup: a company or an ISP. */
  netName?: string | null;
  /** ISP network and an unidentified visitor. */
  isIsp?: boolean;
  /** The contact's organization. */
  organization?: string | null;
  referrerType: string;
  referrerDomain: string;
  utmCampaignName: string;
  utmCampaignSource: string;
  sessionSource: string;
  referrerApp: string;
  topDimension?: string;
  engagementScore: number;
  loginStatus: DataPlatformLoginStatus;
  events: DataPlatformVisitEvent[];
  // Summary fields — populated when includeEvents=false, absent otherwise.
  pageViewCount?: number;
  goalCount?: number;
  goalNames?: string[];
  tags?: string[];
  /** Dimension composites ("type:value") ranked by engagement score descending. */
  topDimensions?: string[];
  /** The engagement score behind each composite in topDimensions. */
  dimensionScores?: Record<string, number>;
  /** Visits this profile has made, this one included. Absent until the backend returns it, which
   *  leaves consumers unable to tell a new visitor from a returning one. */
  visitCount?: number;
  totalRevenue?: number;
  isHoldout?: boolean;
  /** Nothing on the visitor's device backed this visit's uid, so it belongs to no stored visitor. */
  isStateless?: boolean;
  /** The visit ran with the account's traffic rule denying this visitor. */
  isOptOut?: boolean;
  /** Coupons shown during this visit. Null when none were distributed. */
  distributedCoupons?: DataPlatformDistributedCoupon[];
  /** Not part of `events` or `totalRevenue`; null when none. */
  credits?: DataPlatformVisitCredit[] | null;
}
export const CREDIT_EVENT_TYPES = ['attribution-last-touch', 'attribution-incentivized'] as const;
export type DataPlatformCreditType = (typeof CREDIT_EVENT_TYPES)[number];
export interface DataPlatformVisitCredit {
  widgetId: number;
  type: DataPlatformCreditType;
  revenue: number;
}
export interface DataPlatformDistributedCoupon {
  /** Set id for unique-coupon-set codes; null for static shared codes. */
  couponSetId: string | null;
  code: string;
  isCopied: boolean;
}
export interface DataPlatformVisitsResponse {
  visits: DataPlatformVisitRow[];
  /** Null when the request was sent with includeTotalCount=false. */
  totalCount?: number | null;
}

// Profiles
export interface DataPlatformProfilesRequest {
  filter: DataPlatformEventFilter;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  /** When false the server skips the count query and totalCount will be null. Default: true. */
  includeTotalCount?: boolean;
  /** When true, the universe expands to every identified profile (profiles ∪ id_map), including
   *  those with no recent activity. Behavioral filters are ignored server-side in this mode. */
  includeInactive?: boolean;
  /** When true, only contacts that have never been seen on the site (no uid) are returned. */
  notYetSeen?: boolean;
  /** When true, only the total count is computed (no page rows). Used to fetch the count as its own request. */
  countOnly?: boolean;
}
export interface DataPlatformProfileRow {
  uid: number;
  firstSeen: string;
  lastSeen: string;
  totalEvents: number;
  totalSessions: number;
  totalRevenue: number;
  lastBrowser: string;
  lastOs: string;
  lastGeoCountry: string;
  lastGeoCity: string;
  topDimension?: string;
  engagementScore: number;
  tags: string[];
  /** Most recently seen identifier. Empty when the profile is anonymous. */
  latestIdentifier?: string;
  /** Contact display name (first + last), when known. */
  name?: string;
  /** Contact's stored email column, when known. May differ from latestIdentifier. */
  email?: string;
  /** Set only on rows with no uid: a contact never seen on this site, which carries zero stats. */
  contactId?: number;
  // Source/UTM/referrer fields live on DataPlatformProfileDetailsResponse;
  // the flyout fetches them lazily via getProfileDetails.
}
export interface DataPlatformProfileDevice {
  uid: number;
  lastSeen: string;
  browser: string;
  os: string;
}
export interface DataPlatformProfileDetailsRequest {
  canonicalUid: number;
  siteIds: number[];
}
export interface DataPlatformProfileIdentifier {
  value: string;
  source: string;
  type: string;
  /** When the identifier was first linked. Null on imported rows that carry no date. */
  firstSeen: string | null;
}
export interface DataPlatformProfileSplitDevice {
  uid: number;
  /** Null when the device has no events left. */
  lastSeen: string | null;
  browser: string;
  os: string;
  /** The profile's identifiers this device's events carry, in the profile's order. */
  identifiers: string[];
}
export interface DataPlatformProfileSplitOptions {
  firstName: string | null;
  lastName: string | null;
  identifiers: DataPlatformProfileIdentifier[];
  /** The most recently linked devices; deviceCount says how many the profile has. */
  devices: DataPlatformProfileSplitDevice[];
  deviceCount: number;
  /** Tags stay on the original profile. */
  tags: string[];
}
/** identifiers and uids are the ones that move to the new profile. */
export interface DataPlatformProfileSplitRequest {
  identifiers: string[];
  uids: number[];
  newFirstName: string | null;
  newLastName: string | null;
  originalFirstName: string | null;
  originalLastName: string | null;
}
export interface DataPlatformProfileSplitResponse {
  originalUid: number;
  /** Null when no device moved. */
  newUid: number | null;
}
export interface DataPlatformProfileDetailsResponse {
  devices: DataPlatformProfileDevice[];
  identifierDetails: DataPlatformProfileIdentifier[];
  source: DataPlatformProfileSource;
}
export interface DataPlatformProfileSource {
  lastReferrerType: string;
  lastReferrerDomain: string;
  lastUtmCampaignName: string;
  lastUtmCampaignSource: string;
  lastUtmCampaignMedium: string;
  lastSessionSource: string;
  lastReferrerApp: string;
}
export interface DataPlatformProfilesResponse {
  profiles: DataPlatformProfileRow[];
  /** Null when the request was sent with includeTotalCount=false. */
  totalCount?: number | null;
}

// Properties
export interface DataPlatformPropertiesRequest {
  uid: number;
  /**
   * Visit scope: when set, properties come from that session's custom data (the visit's form/survey
   * answers) instead of the profile-wide store. Set by the visit flyout, omitted by the profile
   * flyout; `from`/`to` are then ignored server-side.
   */
  sessionId?: number;
  from?: string;
  to?: string;
}
export interface DataPlatformPropertiesResponse {
  properties: { [key: string]: string };
}

// Visit status
export interface DataPlatformVisitStatusRequest {
  uid: number;
  sessionId: number;
}
export interface DataPlatformVisitStatusResponse {
  /** True once the finalized session row has landed. */
  isFinalized: boolean;
  /** Latest stored event; null when the visit has no rows. */
  lastEventAt: string | null;
  eventCount: number;
}

// Consent events
export interface DataPlatformConsentEventsRequest {
  uid: number;
  from?: string;
  to?: string;
}
export interface DataPlatformConsentEvent {
  timestamp: string;
  goalName: string;
  title?: string;
  currentUrl?: string;
  privacyPolicyUrl?: string;
  privacyPolicyText?: string;
  isCheckboxPresent?: boolean;
  versionDate?: string;
  revisionNumber?: number;
}
export interface DataPlatformConsentEventsResponse {
  events: DataPlatformConsentEvent[];
}

// Filter options
export interface DataPlatformFilterOptions {
  goalOptions: string[];
  tagOptions: string[];
  utmCampaignOptions: string[];
  dimensionOptions: string[];
  widgetEventTypeOptions: string[];
  consentVersionOptions: string[];
  countryOptions: string[];
  sessionSourceOptions: string[];
  referrerAppOptions: string[];
  referrerDomainOptions: string[];
  deviceTypeOptions: string[];
  /** A visit in the window has a known login state; null when unknown. */
  hasLoginData: boolean | null;
  utmSourceOptions: string[];
  utmMediumOptions: string[];
  /** Score events exist in the window, so the NPS / CSAT facets have something to filter. */
  hasNpsScores: boolean;
  hasCsatScores: boolean;
  /** Identifier-source groups present on the site (identity_source breakdown labels). */
  identitySourceOptions: string[];
}

// Consent version dates
export interface DataPlatformConsentVersionDatesRequest {
  siteIds: number[];
}
export interface DataPlatformConsentVersionDatesResponse {
  versions: string[];
}

// Widget campaigns
export interface DataPlatformWidgetCampaign {
  widgetId: number;
  eventType: string;
}
export interface DataPlatformWidgetCampaignsRequest {
  filter: DataPlatformEventFilter;
}
export interface DataPlatformWidgetCampaignsResponse {
  items: DataPlatformWidgetCampaign[];
}

// Audiences
export interface DataPlatformAudienceRule {
  type: string; // event_count | goal_count | purchase_count | revenue | last_active | first_seen | total_events | session_count | has_tag | has_not_tag | dimensions | dimension_affinity | widget_event_count | has_consent | has_not_consent | engagement_score | is_identified | is_anonymous | is_logged_in | is_logged_out | has_all_actions | has_any_action | interest_profile | nps_score | csat_score
  eventName?: string;
  operator?: string; // eq | neq | gt | gte | lt | lte | between; nps_score / csat_score: gt | lt; interest_profile: any_of | not_any_of
  value?: number;
  upperValue?: number; // upper bound for 'between' operator
  // Timeframe (used by revenue, event_count, total_events, widget_event_count, session_count, etc.)
  couponOnly?: boolean; // revenue: restrict to purchases with a coupon code
  daysAgo?: number; // TimeWindow: last N days; BetweenDaysAgo: more-recent end (smaller number)
  daysAgoUpper?: number; // BetweenDaysAgo: older end (larger number)
  startDate?: string; // SinceDate / BetweenDates (ISO 8601)
  endDate?: string; // BetweenDates (ISO 8601)
  tag?: string;
  /** Any of these: goal names, tags, consent version dates or interest profile ids. */
  values?: string[];
  actions?: string[]; // has_all_actions / has_any_action
  dimension?: string;
  widgetId?: number;
  widgetEventType?: string;
  versionDate?: string;
}

export interface DataPlatformAudienceDefinition {
  id: string;
  rules: DataPlatformAudienceRule[];
}

export interface DataPlatformAudienceEvaluateRequest {
  siteIds: number[];
  uid?: number;
  identifier?: string;
  audiences: DataPlatformAudienceDefinition[];
}

export interface DataPlatformAudienceMatch {
  audienceId: string;
  matched: boolean;
}

export interface DataPlatformAudienceEvaluateResponse {
  matches: DataPlatformAudienceMatch[];
}

export interface DataPlatformAudienceCountRequest {
  siteIds: number[];
  audience: DataPlatformAudienceDefinition;
}

export interface DataPlatformAudienceCountResponse {
  audienceId: string;
  count: number;
}

export interface DataPlatformAudienceSizeEstimate {
  low: number;
  centre: number;
  high: number;
}

export interface DataPlatformAudienceUnionMember {
  /** Omitted for everyone on the site. */
  audienceId?: string;
  /** Omitted to count the whole pool. */
  estimate?: DataPlatformAudienceSizeEstimate;
  /** Shared by members narrowed by the same criteria, so a person in several counts once. */
  group?: string;
}

export interface DataPlatformAudienceCountManyRequest {
  siteIds: number[];
  audiences: DataPlatformAudienceDefinition[];
  union?: DataPlatformAudienceUnionMember[];
}

export interface DataPlatformAudienceCountManyResponse {
  /** Null when the request asks for no union. */
  union: DataPlatformAudienceSizeEstimate | null;
}

// Daily audience count (trend chart). One row per calendar day; days with no
// matches are still emitted (count: 0).
export interface DataPlatformAudienceCountDailyRequest {
  siteIds: number[];
  audience: DataPlatformAudienceDefinition;
  /** Inclusive UTC date — YYYY-MM-DD. */
  from: string;
  /** Inclusive UTC date — YYYY-MM-DD. */
  to: string;
}

export interface DataPlatformAudienceCountDailyPoint {
  /** UTC date — YYYY-MM-DD. */
  date: string;
  count: number;
}

export interface DataPlatformAudienceCountDailyResponse {
  audienceId: string;
  points: DataPlatformAudienceCountDailyPoint[];
}

// Insights
export interface DataPlatformInsightsSummaryRequest {
  dateFrom: string;
  dateTo: string;
  language?: string;
  currency?: string;
  force?: boolean;
}

export interface DataPlatformInsightsSummaryResponse {
  insights: string[];
}

export interface DataPlatformInsightsChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface DataPlatformInsightsChatRequest {
  dateFrom: string;
  dateTo: string;
  messages: DataPlatformInsightsChatMessage[];
  language?: string;
  currency?: string;
}

export interface DataPlatformInsightsChatResponse {
  reply: string;
}

export interface DataPlatformAudienceSuggestionsRequest {
  dateFrom: string;
  dateTo: string;
  existingNames?: string[];
  language?: string;
  currency?: string;
}

export interface DataPlatformAudienceSuggestionDto {
  title: string;
  reason: string;
  priority: string;
  rules: DataPlatformAudienceRule[];
  /** Real profile count enriched server-side. null when the count timed out or failed. */
  estimatedSize: number | null;
}

export interface DataPlatformAudienceSuggestionsResponse {
  suggestions: DataPlatformAudienceSuggestionDto[];
}

export interface DataPlatformCampaignSuggestionsRequest {
  language?: string;
  force?: boolean;
}

export interface DataPlatformAnalysedWindow {
  from: string;
  to: string;
  days: number;
  includesToday: boolean;
}

export interface DataPlatformCampaignReference {
  campaignId: number;
  name: string;
}

export interface DataPlatformCampaignSuggestion {
  id: string;
  rule: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  reason: string;
  references?: DataPlatformCampaignReference[];
}

export interface DataPlatformCampaignSuggestionsResponse {
  status: 'ready' | 'insufficient_data' | 'unavailable';
  reason: 'too_few_impressions' | 'too_few_peers' | 'daily_limit' | 'model_failed' | null;
  summary: string | null;
  window?: DataPlatformAnalysedWindow | null;
  suggestions: DataPlatformCampaignSuggestion[];
  generatedAt: string;
}

export interface DataPlatformCampaignSuggestionFeedbackRequest {
  suggestionId: string;
  rule: string;
  rating: 'up' | 'down';
}

// Contacts (Postgres-native)
export interface DataPlatformContactRow {
  contactId: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  tags: string[];
  hasEmailConsent: boolean;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface DataPlatformContactDetailRequest {
  contactId: number;
}

export interface DataPlatformContactIdentifier {
  type: string;
  value: string;
  source: string | null;
  firstSeen: string;
}

export interface DataPlatformContactSubscription {
  channel: string;
  listId: string;
  status: string;
  source: string | null;
  subscribedAt: string | null;
  unsubscribedAt: string | null;
}

export interface DataPlatformContactConsentLog {
  type: string;
  status: string;
  legalBasis: string | null;
  versionDate: string | null;
  recordedAt: string;
  source: string | null;
}

export interface DataPlatformContactDetail {
  contactId: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  tags: string[];
  properties: Record<string, string>;
  consents: Record<string, boolean>;
  identifiers: DataPlatformContactIdentifier[];
  subscriptions: DataPlatformContactSubscription[];
  consentLog: DataPlatformContactConsentLog[];
  createdAt: string;
  updatedAt: string;
}

export interface ContactImportUploadResponse {
  importId: number;
}

export interface ContactImportPreviewField {
  header: string;
  value: string;
}

export interface ContactImportPreviewRow {
  fields: ContactImportPreviewField[];
}

export interface ContactImportValidateResponse {
  rowsForPreview: ContactImportPreviewRow[];
  totalRows: number;
}

export interface ContactImportStatus {
  importId: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalRows: number | null;
  processedRows: number;
  failedRows: number;
  errorSummary: string | null;
}

// Self-describing metadata attached to KPI responses. Optional to consume —
// dashboards can read top-level value fields and ignore meta; agents/tooltips
// can read meta.metrics[fieldName].definition + unit for unit-aware rendering.
export interface DataPlatformMetricDescriptor {
  unit: string;
  displayHint?: string;
  currency?: string;
  numerator?: string;
  denominator?: string;
  range?: { min: number; max: number };
  definition: string;
}

export interface DataPlatformResponseMeta {
  timeframe?: { from: string; to: string };
  grouping: string;
  granularity?: string;
  siteIds?: number[];
  widgetIds?: number[];
  fillEmptyDays?: boolean;
  /** The id of filter.audience, when the request had one. */
  audience?: string;
  metrics: Record<string, DataPlatformMetricDescriptor>;
  /** Present when the window starts before some of what was asked for is recorded. */
  coverage?: DataPlatformDataCoverage;
  /** On last-touch responses: the days after an experience in which a purchase is still credited to it. */
  lastTouchWindowDays?: number;
}

export interface DataPlatformDataCoverage {
  /** YYYY-MM-DD. */
  completeFrom: string;
  affected: string[];
}

// Sales Drivers — site
export interface DataPlatformSalesDriversSiteSummaryRequest {
  siteIds: number[];
  from?: string;
  to?: string;
}

export interface DataPlatformSalesDriversSiteResponse {
  sessions: number;
  purchases: number;
  couponCopies: number;
  revenue: number;
  purchaseRate: number | null;
  averageOrderValue: number | null;
  revenuePerSession: number | null;
  couponCopyRate: number | null;
  meta: DataPlatformResponseMeta;
}

export interface DataPlatformSalesDriversSiteSummaryDailyRequest {
  siteIds: number[];
  from: string;
  to: string;
  fillEmptyDays?: boolean;
}

export interface DataPlatformSalesDriversSiteDailyItem {
  day: string;
  sessions: number;
  purchases: number;
  couponCopies: number;
  revenue: number;
  purchaseRate: number | null;
  averageOrderValue: number | null;
  revenuePerSession: number | null;
  couponCopyRate: number | null;
}

export interface DataPlatformSalesDriversSiteDailyResponse {
  items: DataPlatformSalesDriversSiteDailyItem[];
  meta: DataPlatformResponseMeta;
}

// Sales Drivers — widget
export interface DataPlatformSalesDriversWidgetRequest {
  siteIds: number[];
  from?: string;
  to?: string;
  widgetIds?: number[];
}

export interface DataPlatformSalesDriversWidgetItem {
  widgetId: number;
  opens: number;
  couponCopies: number;
  lastTouchPurchases: number;
  incentivizedPurchases: number;
  lastTouchRevenue: number;
  incentivizedRevenue: number;
  purchaseRate: number | null;
  averageOrderValue: number | null;
  revenuePerOpen: number | null;
  couponCopyRate: number | null;
}

export interface DataPlatformSalesDriversWidgetResponse {
  items: DataPlatformSalesDriversWidgetItem[];
  meta: DataPlatformResponseMeta;
}

export interface DataPlatformSalesDriversWidgetDailyRequest {
  siteIds: number[];
  from: string;
  to: string;
  widgetIds?: number[];
  fillEmptyDays?: boolean;
}

export interface DataPlatformSalesDriversWidgetDailyItem extends DataPlatformSalesDriversWidgetItem {
  day: string;
}

export interface DataPlatformSalesDriversWidgetDailyResponse {
  items: DataPlatformSalesDriversWidgetDailyItem[];
  meta: DataPlatformResponseMeta;
}

// Customer Feedback — NPS / CSAT
export interface DataPlatformCustomerFeedbackNpsScore {
  responses: number;
  average: number | null;
  promoters: number;
  passives: number;
  detractors: number;
  npsValue: number | null;
}

export interface DataPlatformCustomerFeedbackCsatScore {
  responses: number;
  average: number | null;
  satisfied: number;
  satisfiedPercentage: number | null;
}

export interface DataPlatformCustomerFeedbackScoresRequest {
  siteIds: number[];
  from?: string;
  to?: string;
}

export interface DataPlatformCustomerFeedbackScoresResponse {
  nps: DataPlatformCustomerFeedbackNpsScore;
  csat: DataPlatformCustomerFeedbackCsatScore;
  meta: DataPlatformResponseMeta;
}

export interface DataPlatformCustomerFeedbackScoresDailyRequest {
  siteIds: number[];
  from: string;
  to: string;
  fillEmptyDays?: boolean;
}

export interface DataPlatformCustomerFeedbackScoresDailyItem {
  day: string;
  nps: DataPlatformCustomerFeedbackNpsScore;
  csat: DataPlatformCustomerFeedbackCsatScore;
}

export interface DataPlatformCustomerFeedbackScoresDailyResponse {
  items: DataPlatformCustomerFeedbackScoresDailyItem[];
  meta: DataPlatformResponseMeta;
}

export interface DataPlatformCustomerFeedbackByCustomerRequest {
  siteIds: number[];
  uid?: number;
  identifier?: string;
}

export interface DataPlatformCustomerFeedbackByCustomerItem {
  siteId: number;
  uid: number;
  latestNps: number | null;
  latestCsat: number | null;
}

export interface DataPlatformCustomerFeedbackByCustomerResponse {
  items: DataPlatformCustomerFeedbackByCustomerItem[];
  meta: DataPlatformResponseMeta;
}

// Benchmarks — platform-wide macro-averages across all eligible accounts over
// a 3/6/12-month window. Rates are pre-scaled percentages (0..100);
// assistedSalesPerCampaign is an absolute number. value is null when no
// eligible account/campaign had valid data for the metric in the window.
export type DataPlatformBenchmarkTimeframe = 3 | 6 | 12;

export type DataPlatformBenchmarkMetricKey =
  'responseRate' | 'ctr' | 'purchaseRate' | 'identificationRate' | 'assistedSalesPerCampaign';

export interface DataPlatformBenchmarkValue {
  value: number | null;
  unit: string;
}

export interface DataPlatformBenchmarksResponse {
  timeframe: { from: string; to: string };
  benchmarks: Record<DataPlatformBenchmarkMetricKey, DataPlatformBenchmarkValue>;
  lastUpdated: string;
  meta: DataPlatformResponseMeta;
}

// Form Response Analytics — aggregate submission datapoints across one or more sites.
export interface DataPlatformFormResponseDatapointsRequest {
  siteIds: number[];
  from?: string;
  to?: string;
}

export interface DataPlatformFormResponseDatapointsResponse {
  totalSubmissions: number;
  totalDatapoints: number;
  daily: DataPlatformFormResponseDatapointsDay[];
}

export interface DataPlatformFormResponseDatapointsDay {
  date: string;
  submissions: number;
  datapoints: number;
}

/** How many people a delete would erase, and across how many accounts. */
export interface ValidateDeleteProfilesResponse {
  /** People. Never contacts + uids: a contact is a person, its uids are that person's devices. */
  matchedPersons: number;
  matchedContacts: number;
  matchedUids: number;
  siteCount: number;
  capped: boolean;
}

export interface DeleteProfilesResponse {
  /** People. Never contacts + uids. */
  matchedPersons: number;
  matchedContacts: number;
  matchedUids: number;
  completed: boolean;
  /** A by-date run hit its per-call cap; run again to continue. */
  capped: boolean;
}
